import { useEffect, useMemo, useState } from 'react';
import {
  operatorConsole,
  type PackBuilderItemsResponse,
  type PackBuilderSort,
} from '../../../lib/api/operatorConsole';

export const PACK_ITEMS_DEBOUNCE_MS = 300;
export const PACK_ITEMS_PER_PAGE = 50;

export interface PackItemsQuery {
  q: string;
  sort: PackBuilderSort;
}

export interface UsePackItemsResult {
  data: PackBuilderItemsResponse | null;
  loading: boolean;
  error: string | null;
}

function isAbort(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name: unknown }).name === 'AbortError'
  );
}

/**
 * The pack's CONTENTS, read from Postgres via packs/:id/items — every member
 * whatever its engagement_status. This is deliberately NOT the Elasticsearch
 * inventory: a card a player pulled leaves the index but stays in the pack.
 *
 * Same shape as useInventory: a 300 ms debounce on every query change, the
 * previous request aborted so a slow answer cannot paint over a newer one, and
 * `version` to refetch after a membership write. `query` does not have to be a
 * stable object — the request params are compared by value.
 */
export function usePackItems(
  packId: number,
  query: PackItemsQuery,
  page: number,
  version: number,
): UsePackItemsResult {
  const [data, setData] = useState<PackBuilderItemsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify({
    page,
    per: PACK_ITEMS_PER_PAGE,
    sort: query.sort,
    ...(query.q.trim() ? { q: query.q.trim() } : {}),
  });
  const params = useMemo(
    () => JSON.parse(key) as { page: number; per: number; sort: PackBuilderSort; q?: string },
    [key],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      operatorConsole.packBuilder
        .listItems(packId, params, controller.signal)
        .then((res) => {
          if (cancelled) return;
          setData(res);
          setError(null);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (cancelled || isAbort(err)) return;
          setError(err instanceof Error ? err.message : 'Unknown error');
          setLoading(false);
        });
    }, PACK_ITEMS_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [packId, params, version]);

  return { data, loading, error };
}
